import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme';

function CategoryModal({ isOpen, onClose, onAdd, editingCategory, sizes = [] }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedSizes: [],
    usesPerSizePricing: false,
  });

  useEffect(() => {
    if (editingCategory && isOpen) {
      const savedSizeIds = (editingCategory.sizes || []).map(s => s.id);
      setFormData({
        name: editingCategory.name || '',
        description: editingCategory.description || '',
        selectedSizes: savedSizeIds,
        usesPerSizePricing: editingCategory.usesPerSizePricing || false,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        selectedSizes: [],
        usesPerSizePricing: false,
      });
    }
  }, [editingCategory, isOpen, sizes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Helper function to detect if a size is an inch size
  const isInchSize = (sizeObj) => {
    const name = sizeObj.name.trim();
    // Inch sizes: contain quotes OR have decimal points (0.5, 1.0, 2.0, 3.0)
    return name.includes('"') || /^\d+\.\d+$/.test(name);
  };

  // Helper function to detect if a size is a slippers size
  const isSlippersSize = (sizeObj) => {
    const name = sizeObj.name.trim();
    // Slippers sizes: numeric single or double digit from 5-13
    const num = parseInt(name);
    return !isNaN(num) && num >= 5 && num <= 13 && /^\d+$/.test(name);
  };

  // Helper function to check if a size group is fully selected
  const isGroupSelected = (sizeGroup) => {
    let groupSizeIds;
    
    if (sizeGroup === 'inch') {
      groupSizeIds = sizes.filter(isInchSize).map(s => s.id);
    } else if (sizeGroup === 'slippers') {
      groupSizeIds = sizes.filter(isSlippersSize).map(s => s.id);
    } else {
      groupSizeIds = sizes.filter(s => !isInchSize(s) && !isSlippersSize(s) && s.name !== 'XS').map(s => s.id);
    }
    
    // All sizes in this group must be in selectedSizes
    if (groupSizeIds.length === 0) return false;
    return groupSizeIds.every(id => formData.selectedSizes.includes(id));
  };

  const handleSizeToggle = (sizeGroup) => {
    // sizeGroup is either 'inch', 'slippers', or 'standard'
    let groupSizes;
    
    if (sizeGroup === 'inch') {
      // Inch sizes: decimal numbers (0.5, 1.0, 2.0, 3.0)
      groupSizes = sizes.filter(isInchSize).map(s => s.id);
    } else if (sizeGroup === 'slippers') {
      // Slippers sizes: 5, 6, 7, 8, 9, 10, 11, 12, 13
      groupSizes = sizes.filter(isSlippersSize).map(s => s.id);
    } else {
      // Standard sizes: S, M, L, XL, XXL (exclude XS)
      groupSizes = sizes
        .filter(s => !isInchSize(s) && !isSlippersSize(s) && s.name !== 'XS')
        .map(s => s.id);
    }
    
    // Check if all sizes in this group are already selected
    const allSelected = groupSizes.every(id => formData.selectedSizes.includes(id));
    
    if (allSelected) {
      // Deselect all in this group
      setFormData({
        ...formData,
        selectedSizes: formData.selectedSizes.filter(id => !groupSizes.includes(id)),
      });
    } else {
      // Select all in this group
      const newSelected = [...formData.selectedSizes];
      groupSizes.forEach(id => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
      setFormData({
        ...formData,
        selectedSizes: newSelected,
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }

    onAdd({
      id: editingCategory?.id,
      name: formData.name,
      description: formData.description,
      sizesData: formData.selectedSizes,
      usesPerSizePricing: formData.usesPerSizePricing,
    });

    setFormData({
      name: '',
      description: '',
      selectedSizes: [],
      usesPerSizePricing: false,
    });
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: spacing.lg,
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows['2xl'],
          padding: spacing.xl,
          width: '100%',
          maxWidth: '42rem',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.text.primary, margin: 0 }}>
            🏷️ {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.text.secondary,
              padding: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </motion.button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
              Category Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Ortho"
              required
              style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                fontSize: '0.95rem',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                e.target.style.borderColor = colors.primary[600];
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = colors.border;
              }}
            />
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.sm }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Category description..."
              rows="2"
              style={{
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                fontSize: '0.95rem',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                e.target.style.borderColor = colors.primary[600];
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = colors.border;
              }}
            />
          </div>

          <div style={{ marginBottom: spacing.lg, padding: spacing.md, backgroundColor: '#f0f4ff', borderRadius: '0.5rem', border: `2px solid ${colors.primary[300]}` }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: spacing.md, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.usesPerSizePricing}
                onChange={(e) => setFormData({ ...formData, usesPerSizePricing: e.target.checked })}
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  cursor: 'pointer',
                  accentColor: colors.primary[600],
                }}
              />
              <div>
                <span style={{ fontSize: '0.95rem', fontWeight: 700, color: colors.text.primary }}>
                  💰 This category uses per-size pricing
                </span>
                <p style={{ fontSize: '0.75rem', color: colors.text.secondary, margin: `${spacing.xs} 0 0 0` }}>
                  When enabled, each size will have its own MRP, cost, discount, and selling price (like Tapes)
                </p>
              </div>
            </label>
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.md }}>
              Available Sizes for This Category
            </label>
            <p style={{ fontSize: '0.75rem', color: colors.text.secondary, marginBottom: spacing.md, margin: 0 }}>
              Select sizes that will auto-appear when products use this category
            </p>
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: '0.5rem', padding: spacing.lg, backgroundColor: colors.primary[50] }}>
              {sizes && sizes.length > 0 ? (
                <div style={{ display: 'grid', gap: spacing.lg }}>
                  {/* Standard Sizes Group */}
                  {sizes.some(s => !isInchSize(s) && !isSlippersSize(s) && s.name !== 'XS') && (
                    <motion.div
                      whileHover={{ boxShadow: shadows.sm }}
                      style={{
                        border: `2px dashed ${colors.primary[300]}`,
                        padding: spacing.md,
                        borderRadius: '0.5rem',
                        backgroundColor: 'white',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: spacing.md, cursor: 'pointer', marginBottom: spacing.md }}>
                        <input
                          type="checkbox"
                          checked={isGroupSelected('standard')}
                          onChange={() => handleSizeToggle('standard')}
                          style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            cursor: 'pointer',
                            accentColor: colors.primary[600],
                          }}
                        />
                        <span style={{ fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary }}>👕 Standard Sizes</span>
                      </label>
                      <div style={{ display: 'grid', gap: spacing.xs, marginLeft: spacing.xl }}>
                        {sizes.filter(s => !isInchSize(s) && !isSlippersSize(s) && s.name !== 'XS').map(s => (
                          <div key={s.id} style={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                            ✓ {s.name}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Inch Sizes Group */}
                  {sizes.some(isInchSize) && (
                    <motion.div
                      whileHover={{ boxShadow: shadows.sm }}
                      style={{
                        border: `2px dashed ${colors.primary[300]}`,
                        padding: spacing.md,
                        borderRadius: '0.5rem',
                        backgroundColor: 'white',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: spacing.md, cursor: 'pointer', marginBottom: spacing.md }}>
                        <input
                          type="checkbox"
                          checked={isGroupSelected('inch')}
                          onChange={() => handleSizeToggle('inch')}
                          style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            cursor: 'pointer',
                            accentColor: colors.primary[600],
                          }}
                        />
                        <span style={{ fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary }}>📏 Inch Sizes</span>
                      </label>
                      <div style={{ display: 'grid', gap: spacing.xs, marginLeft: spacing.xl }}>
                        {sizes.filter(isInchSize).map(s => (
                          <div key={s.id} style={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                            ✓ {s.name}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Slippers Sizes Group */}
                  {sizes.some(isSlippersSize) && (
                    <motion.div
                      whileHover={{ boxShadow: shadows.sm }}
                      style={{
                        border: `2px dashed ${colors.primary[300]}`,
                        padding: spacing.md,
                        borderRadius: '0.5rem',
                        backgroundColor: 'white',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: spacing.md, cursor: 'pointer', marginBottom: spacing.md }}>
                        <input
                          type="checkbox"
                          checked={isGroupSelected('slippers')}
                          onChange={() => handleSizeToggle('slippers')}
                          style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            cursor: 'pointer',
                            accentColor: colors.primary[600],
                          }}
                        />
                        <span style={{ fontSize: '0.875rem', fontWeight: 700, color: colors.text.primary }}>👟 Slippers Sizes</span>
                      </label>
                      <div style={{ display: 'grid', gap: spacing.xs, marginLeft: spacing.xl }}>
                        {sizes.filter(isSlippersSize).map(s => (
                          <div key={s.id} style={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                            ✓ {s.name}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              ) : (
                <p style={{ color: colors.text.secondary, fontSize: '0.875rem', margin: 0 }}>No sizes available</p>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: spacing.lg }}>
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                flex: 1,
                backgroundColor: colors.primary[500],
                color: 'white',
                padding: `${spacing.md} ${spacing.lg}`,
                borderRadius: '0.75rem',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[600];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[500];
              }}
            >
              {editingCategory ? '✓ Update Category' : '✓ Add Category'}
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              style={{
                flex: 1,
                backgroundColor: colors.text.tertiary,
                color: colors.text.primary,
                padding: `${spacing.md} ${spacing.lg}`,
                borderRadius: '0.75rem',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default CategoryModal;
